import { Component } from "react";
import { useLocation } from "react-router-dom";

import { esTrozoQueNoLlega } from "@/lib/trozoQueNoLlega.mjs";

/**
 * Lo que se ve cuando una pantalla revienta, en vez de una página en blanco.
 *
 * QUÉ PASABA. No había ni uno en toda la plataforma. Cualquier error al pintar —y el más normal
 * de todos, que un trozo `lazy` no llegue— desmontaba la aplicación entera y dejaba la pantalla
 * en blanco, sin barra, sin pie y sin un botón. En el ordenador casi no pasa. En el móvil pasa
 * cada semana: la pestaña se queda dormida en segundo plano, publicamos, el alumno vuelve, toca
 * un enlace y el trozo que pide ya no existe en el servidor (`ChunkLoadError`). Para él la
 * plataforma «se ha roto».
 *
 * DOS CASOS, DOS FRASES:
 *   · EL TROZO NO LLEGA: es que hay una versión nueva o que la red va a medias. Se dice eso y se
 *     ofrece recargar, que lo arregla siempre. **No se recarga solo**: hacerlo sin preguntar
 *     necesitaría apuntar en el navegador que ya se intentó —una llave más que declarar— para
 *     no entrar en un bucle sin red, y un botón cuesta un toque.
 *   · CUALQUIER OTRO ERROR: se dice que esta pantalla ha fallado y se ofrece volver a probar o
 *     ir al inicio. La barra y el pie siguen ahí, porque esto envuelve solo las rutas.
 *
 * SE REARMA AL CAMBIAR DE DIRECCIÓN, pero SIN `key`: con la ruta de `key`, cada cambio de
 * dirección desmontaría y volvería a montar todas las pantallas —también `/leccion/a` →
 * `/leccion/b`, y también el mundo 3D, que tiene un lienzo que no puede perderse a cada paso—.
 * Así que el guardián se queda montado y lo único que hace al cambiar la dirección es olvidar el
 * error, si lo había.
 *
 * VA EN LA PRIMERA CARGA y por eso no importa nada más que React y el router, que ya están.
 * Cómo se reconoce un trozo que no llega está en `lib/trozoQueNoLlega.mjs`, para probarlo en node.
 */
class Guardian extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(antes) {
    if (this.state.error && antes.ruta !== this.props.ruta) this.setState({ error: null });
  }

  componentDidCatch(error) {
    // A LA CONSOLA Y NADA MÁS: es lo que se le pide a quien nos escribe «se ha quedado en blanco».
    try { console.error("[pantalla]", error); } catch { /* sin consola */ }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const trozo = esTrozoQueNoLlega(error);
    return (
      <section className="max-w-[760px] mx-auto px-6 lg:px-10 py-20" role="alert" data-testid="pantalla-que-falla">
        <h1 className="font-serif-display text-4xl leading-tight text-alpha-black mb-4">
          {trozo ? "Hay una versión nueva" : "Esta pantalla ha fallado"}
        </h1>
        <p className="text-[16px] leading-relaxed text-alpha-black/75 max-w-[62ch] mb-8">
          {trozo
            ? "Esta parte no ha llegado: o la red va a medias, o hemos actualizado la plataforma "
              + "mientras la tenías abierta. Recargar lo arregla."
            : "No has hecho nada mal. Puedes volver a intentarlo o ir a la portada; lo que tenías "
              + "guardado sigue ahí."}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <button type="button"
            className="min-h-[44px] rounded-full bg-alpha-red px-5 text-[12px] font-mono-alpha uppercase tracking-wide text-white"
            onClick={() => (trozo ? window.location.reload() : this.setState({ error: null }))}>
            {trozo ? "Recargar" : "Volver a intentarlo"}
          </button>
          <a href="/"
            className="min-h-[44px] inline-flex items-center rounded-full border border-alpha-black/15 px-5 text-[12px] font-mono-alpha uppercase tracking-wide text-alpha-black/65">
            La portada
          </a>
        </div>
      </section>
    );
  }
}

export default function TrozoQueNoLlega({ children }) {
  const { pathname } = useLocation();
  return <Guardian ruta={pathname}>{children}</Guardian>;
}
